// app/javascript/controllers/charity_eft_payout_controller.js
import { Controller } from "@hotwired/stimulus"
import Swal from "sweetalert2"

export default class extends Controller {
    static values = {
        url: String,
        statusUrl: String,
        name: String,
        amount: Number
    }

    static targets = ["button"]

    connect() {
        if (!this.hasButtonTarget && this.element.tagName === "BUTTON") {
            this.buttonTarget = this.element
        }
    }

    pay(event) {
        event.preventDefault()

        const amountCad = (this.amountValue / 100.0).toFixed(2)
        const charityName = this.nameValue

        Swal.fire({
            title: "Confirm EFT payout",
            html: `
        <p>Send <strong>${amountCad} CAD</strong> to</p>
        <p><strong>${charityName}</strong> by bank transfer?</p>
      `,
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Yes, send payout",
            cancelButtonText: "Cancel"
        }).then(result => {
            if (result.isConfirmed) {
                this._submit()
            }
        })
    }

    async _submit() {
        const button = this.buttonTarget || this.element
        const originalText = button.innerHTML
        let confirmationChecked = false
        let confirmationAmbiguous = false

        button.disabled = true
        button.innerHTML = "Processing..."

        try {
            const response = await fetch(this.urlValue, {
                method: "POST",
                headers: {
                    "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || "",
                    "Accept": "application/json"
                },
                credentials: "same-origin"
            })

            const data = await this._jsonBody(response)

            if (response.ok && data?.status === "ok") {
                this._showSuccess(data.message)
                return
            }

            // A proper Wise rejection is safe to display immediately. It means
            // the server confirmed that the transfer was not funded.
            if (data?.status === "error") {
                confirmationChecked = true
                throw new Error(data.message || "Wise could not send this payout.")
            }

            // Rails/proxy failures can replace the JSON response after Wise
            // has already funded the transfer. Verify the database state before
            // ever presenting an error that might encourage a duplicate retry.
            confirmationChecked = true
            if (await this._waitForPaidConfirmation()) {
                this._showSuccess()
                return
            }

            confirmationAmbiguous = true
            throw new Error("The payout could not be confirmed. Do not send it again yet; refresh the page and verify its status in Wise.")
        } catch (error) {
            console.error(error)

            if (!confirmationChecked && await this._waitForPaidConfirmation()) {
                this._showSuccess()
                return
            }

            if (!confirmationChecked) confirmationAmbiguous = true

            Swal.fire({
                title: confirmationAmbiguous ? "Payout status unclear" : "Payout not sent",
                text: error.message || "Wise could not send this payout. Please review the bank details and try again.",
                icon: confirmationAmbiguous ? "warning" : "error"
            })
        } finally {
            button.disabled = false
            button.innerHTML = originalText
        }
    }

    async _jsonBody(response) {
        const text = await response.text()
        if (!text.trim()) return null

        try {
            return JSON.parse(text)
        } catch (_error) {
            return null
        }
    }

    async _waitForPaidConfirmation() {
        if (!this.hasStatusUrlValue) return false

        const delays = [0, 750, 1500, 2500]

        for (const delay of delays) {
            if (delay > 0) await this._sleep(delay)
            if (await this._isRecordedAsPaid()) return true
        }

        return false
    }

    async _isRecordedAsPaid() {
        try {
            const response = await fetch(this.statusUrlValue, {
                headers: { "Accept": "application/json" },
                credentials: "same-origin",
                cache: "no-store"
            })
            if (!response.ok) return false

            const data = await this._jsonBody(response)
            return data?.status === "paid"
        } catch (_error) {
            return false
        }
    }

    _showSuccess(message) {
        Swal.fire({
            title: "Payout sent",
            text: message || "The charity EFT payout was sent successfully.",
            icon: "success"
        }).then(() => {
            window.location.reload()
        })
    }

    _sleep(milliseconds) {
        return new Promise(resolve => window.setTimeout(resolve, milliseconds))
    }
}
